// src/lib/storeManagement.js
// Store management system for multiple stores with store managers

import { db } from './firebase';
import { 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  setDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy, 
  serverTimestamp 
} from 'firebase/firestore';

/**
 * Store Types
 */
export const STORE_TYPES = {
  COOPERATIVE: 'cooperative',
  MEMBER: 'member', 
  ESTABLISHMENT: 'establishment'
};

/**
 * Store Manager Roles
 */
export const STORE_MANAGER_ROLES = {
  COOPERATIVE_STORE_MANAGER: 'cooperative_store_manager',
  MEMBER_STORE_MANAGER: 'member_store_manager',
  ESTABLISHMENT_STORE_MANAGER: 'establishment_store_manager'
};

/**
 * Store Status
 */
export const STORE_STATUS = {
  ACTIVE: 'active',
  INACTIVE: 'inactive',
  SUSPENDED: 'suspended',
  PENDING: 'pending'
};

/**
 * Create a new store
 */
export async function createStore(storeData) {
  try {
    const storeRef = doc(collection(db, 'stores'));
    const store = {
      id: storeRef.id,
      name: storeData.name,
      type: storeData.type, // STORE_TYPES
      description: storeData.description || '',
      address: storeData.address || '',
      phone: storeData.phone || '',
      email: storeData.email || '',
      managerUid: storeData.managerUid || null,
      status: STORE_STATUS.ACTIVE,
      settings: {
        allowOnlineOrders: true,
        allowPickup: true,
        allowDelivery: true,
        operatingHours: storeData.operatingHours || {},
        ...storeData.settings
      },
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    await setDoc(storeRef, store);
    return { success: true, store };
  } catch (error) {
    console.error('Error creating store:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Get store by ID
 */
export async function getStore(storeId) {
  try {
    const storeRef = doc(db, 'stores', storeId);
    const storeSnap = await getDoc(storeRef);
    
    if (storeSnap.exists()) {
      return { success: true, store: { id: storeSnap.id, ...storeSnap.data() } };
    } else {
      return { success: false, error: 'Store not found' };
    }
  } catch (error) {
    console.error('Error getting store:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Get all stores
 */
export async function getAllStores() {
  try {
    const storesRef = collection(db, 'stores');
    const q = query(storesRef, orderBy('createdAt', 'desc'));
    const storesSnap = await getDocs(q);
    
    const stores = storesSnap.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    return { success: true, stores };
  } catch (error) {
    console.error('Error getting stores:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Get stores by type
 */
export async function getStoresByType(storeType) {
  try {
    const storesRef = collection(db, 'stores');
    const q = query(
      storesRef, 
      where('type', '==', storeType),
      orderBy('createdAt', 'desc')
    );
    const storesSnap = await getDocs(q);
    
    const stores = storesSnap.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    return { success: true, stores };
  } catch (error) {
    console.error('Error getting stores by type:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Get stores managed by a user
 */
export async function getStoresByManager(managerUid) {
  try {
    const storesRef = collection(db, 'stores');
    const q = query(
      storesRef, 
      where('managerUid', '==', managerUid),
      orderBy('createdAt', 'desc')
    );
    const storesSnap = await getDocs(q);
    
    const stores = storesSnap.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    return { success: true, stores };
  } catch (error) {
    console.error('Error getting stores by manager:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Update store
 */
export async function updateStore(storeId, updateData) {
  try {
    const storeRef = doc(db, 'stores', storeId);
    const update = {
      ...updateData,
      updatedAt: serverTimestamp()
    };
    
    await updateDoc(storeRef, update);
    return { success: true };
  } catch (error) {
    console.error('Error updating store:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Assign store manager
 */
export async function assignStoreManager(storeId, managerUid, managerRole) {
  try {
    const storeRef = doc(db, 'stores', storeId);
    const userRef = doc(db, 'users', managerUid);
    
    // Update the store with manager information
    const storeUpdate = {
      managerUid,
      managerRole,
      updatedAt: serverTimestamp()
    };
    
    // Get current user data to update roles
    const userSnap = await getDoc(userRef);
    if (!userSnap.exists()) {
      throw new Error('User not found');
    }
    
    const userData = userSnap.data();
    const currentRoles = userData.roles || [];
    
    // Add the store manager role if not already present
    if (!currentRoles.includes(managerRole)) {
      const updatedRoles = [...currentRoles, managerRole];
      
      // Update user with new role
      await updateDoc(userRef, {
        roles: updatedRoles,
        updatedAt: serverTimestamp()
      });
    }
    
    // Update the store
    await updateDoc(storeRef, storeUpdate);
    
    return { success: true };
  } catch (error) {
    console.error('Error assigning store manager:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Remove store manager
 */
export async function removeStoreManager(storeId) {
  try {
    const storeRef = doc(db, 'stores', storeId);
    
    // Get the current store data to find the manager
    const storeSnap = await getDoc(storeRef);
    if (!storeSnap.exists()) {
      throw new Error('Store not found');
    }
    
    const storeData = storeSnap.data();
    const managerUid = storeData.managerUid;
    const managerRole = storeData.managerRole;
    
    // Remove the role from the user's profile if they exist
    if (managerUid && managerRole) {
      const userRef = doc(db, 'users', managerUid);
      const userSnap = await getDoc(userRef);
      
      if (userSnap.exists()) {
        const userData = userSnap.data();
        const currentRoles = userData.roles || [];
        
        // Remove the store manager role
        const updatedRoles = currentRoles.filter(role => role !== managerRole);
        
        // Update user with removed role
        await updateDoc(userRef, {
          roles: updatedRoles,
          updatedAt: serverTimestamp()
        });
      }
    }
    
    // Update the store to remove manager
    const update = {
      managerUid: null,
      managerRole: null,
      updatedAt: serverTimestamp()
    };
    
    await updateDoc(storeRef, update);
    return { success: true };
  } catch (error) {
    console.error('Error removing store manager:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Delete store
 */
export async function deleteStore(storeId) {
  try {
    const storeRef = doc(db, 'stores', storeId);
    await deleteDoc(storeRef);
    return { success: true };
  } catch (error) {
    console.error('Error deleting store:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Check if user is a store manager (any store)
 */
export function isStoreManager(userRoles) {
  if (!userRoles || !Array.isArray(userRoles)) return false;
  
  return userRoles.some(role => 
    role.startsWith('store_manager_') || Object.values(STORE_MANAGER_ROLES).includes(role)
  );
}

/**
 * Check if user is a manager of a specific store
 */
export function isManagerOfStore(userRoles, storeId) {
  if (!userRoles || !Array.isArray(userRoles) || !storeId) return false;
  
  return userRoles.includes(`store_manager_${storeId}`);
}

/**
 * Get store type from manager role
 */
export function getStoreTypeFromRole(role) {
  switch (role) {
    case STORE_MANAGER_ROLES.COOPERATIVE_STORE_MANAGER:
      return STORE_TYPES.COOPERATIVE;
    case STORE_MANAGER_ROLES.MEMBER_STORE_MANAGER:
      return STORE_TYPES.MEMBER;
    case STORE_MANAGER_ROLES.ESTABLISHMENT_STORE_MANAGER:
      return STORE_TYPES.ESTABLISHMENT;
    default:
      return null;
  }
}

/**
 * Get manager role from store type
 */
export function getManagerRoleFromStoreType(storeType) {
  switch (storeType) {
    case STORE_TYPES.COOPERATIVE:
      return STORE_MANAGER_ROLES.COOPERATIVE_STORE_MANAGER;
    case STORE_TYPES.MEMBER:
      return STORE_MANAGER_ROLES.MEMBER_STORE_MANAGER;
    case STORE_TYPES.ESTABLISHMENT:
      return STORE_MANAGER_ROLES.ESTABLISHMENT_STORE_MANAGER;
    default:
      return null;
  }
}

/**
 * Check if user can manage a specific store
 */
export async function canManageStore(userUid, userRoles, storeId) {
  try {
    // Admin can manage any store
    if (userRoles?.includes('admin')) {
      return { canManage: true, reason: 'admin_access' };
    }

    // Check if user has store-specific manager role for this store
    if (isManagerOfStore(userRoles, storeId)) {
      return { canManage: true, reason: 'store_specific_manager_role' };
    }

    // Get store details for additional checks
    const storeResult = await getStore(storeId);
    if (!storeResult.success) {
      return { canManage: false, reason: 'store_not_found' };
    }

    const store = storeResult.store;

    // Check if user is the assigned manager (fallback for legacy data)
    if (store.managerUid === userUid) {
      return { canManage: true, reason: 'assigned_manager' };
    }

    // Legacy check: Check if user has the appropriate store manager role (for backward compatibility)
    const requiredRole = getManagerRoleFromStoreType(store.type);
    if (userRoles?.includes(requiredRole)) {
      return { canManage: true, reason: 'legacy_store_manager_role' };
    }

    return { canManage: false, reason: 'insufficient_permissions' };
  } catch (error) {
    console.error('Error checking store management permissions:', error);
    return { canManage: false, reason: 'error_checking_permissions' };
  }
}

/**
 * Check if user is a store manager or admin
 */
export function isStoreManagerOrAdmin(userRoles) {
  if (!userRoles || !Array.isArray(userRoles)) return false;
  
  return userRoles.some(role => 
    role === 'admin' || Object.values(STORE_MANAGER_ROLES).includes(role)
  );
}
